import React from 'react';
import { Activity, Building2, Cpu } from 'lucide-react';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import { useRole } from '../../context/RoleContext';
import { useAdminData } from '../../context/AdminDataContext';
import { toAuditLogViewModel, toDeviceViewModel } from '../../lib/api/adapters';

const AdminDashboard: React.FC = () => {
  const { role, activeCompanyId } = useRole();
  const { clients, devices, logsByClient, fetchClientLogs, loading, error } = useAdminData();

  const activeClient = clients.find((client) => client.id === activeCompanyId) ?? clients[0] ?? null;
  const companyDevices = devices.filter((device) => device.client_id === activeClient?.id).map(toDeviceViewModel);
  const activeDevices = companyDevices.filter((device) => device.status === 'ACTIVE');

  React.useEffect(() => {
    if (activeClient?.id && !logsByClient[activeClient.id]) {
      fetchClientLogs(activeClient.id).catch(() => undefined);
    }
  }, [activeClient?.id, fetchClientLogs, logsByClient]);

  const latestLogs = (activeClient?.id ? logsByClient[activeClient.id] : []) || [];
  const mappedLogs = latestLogs.slice(0, 6).map(toAuditLogViewModel);

  const topCards =
    role === 'super_admin'
      ? [
          {
            title: 'Clients',
            value: `${clients.length}`,
            subtitle: 'Loaded from /admin/api/listClients',
            icon: Building2,
          },
          {
            title: 'Devices',
            value: `${devices.length}`,
            subtitle: `${devices.filter((device) => device.status === 'ACTIVE').length} currently active`,
            icon: Cpu,
          },
          {
            title: 'Recent Log Events',
            value: `${latestLogs.length}`,
            subtitle: activeClient ? `Fetched for ${activeClient.name}` : 'Select a client to load logs',
            icon: Activity,
          },
        ]
      : [
          {
            title: 'Selected Client',
            value: activeClient?.name ?? 'No client selected',
            subtitle: activeClient?.id ?? 'N/A',
            icon: Building2,
          },
          {
            title: 'Active Devices',
            value: `${activeDevices.length}`,
            subtitle: `${companyDevices.length} total for this client`,
            icon: Cpu,
          },
          {
            title: 'Recent Log Events',
            value: `${latestLogs.length}`,
            subtitle: 'Most recent pull from /admin/api/listClientLogs',
            icon: Activity,
          },
        ];

  return (
    <div className="space-y-10">
      <section className="admin-page-header">
        <div>
          <h1 className="text-4xl font-semibold">
            {role === 'super_admin' ? 'Operations Overview' : `${activeClient?.name ?? 'Client'} Overview`}
          </h1>
          <p className="mt-2 text-base text-[var(--admin-muted)]">
            A clear view of clients, devices and recent activity.
          </p>
        </div>
      </section>

      {error ? (
        <div className="rounded-2xl border border-[var(--admin-rose)] bg-[var(--admin-rose-soft)] px-4 py-3 text-sm text-[var(--admin-rose)]">
          Failed to load admin data: {error}
        </div>
      ) : null}

      <section className="admin-page-kpis">
        {topCards.map((card) => (
          <Card key={card.title} className="bg-[var(--admin-surface-strong)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">{card.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-2xl font-semibold">{card.value}</p>
                  <p className="mt-2 text-sm text-[var(--admin-muted)]">{card.subtitle}</p>
                </div>
                <div className="rounded-2xl bg-[var(--admin-cyan-soft)] p-3 text-[var(--admin-cyan)]">
                  <card.icon size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(20rem,0.65fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Recent Audit Logs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {mappedLogs.length > 0 ? (
              mappedLogs.map((log) => (
                <div key={log.id} className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold">{log.action}</p>
                    <Badge variant="outline">{log.resourceType}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-[var(--admin-muted)]">
                    Actor: {log.actorType}
                    {log.actorId ? ` (${log.actorId})` : ''}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[var(--admin-muted)]">
                    {log.createdAt}
                  </p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[var(--admin-border)] px-4 py-10 text-center text-sm text-[var(--admin-muted)]">
                {loading ? 'Loading logs...' : 'No logs loaded yet for the selected client.'}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Device Status Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-2xl bg-[#171513] px-4 py-4 text-stone-100">
              <p className="text-xs uppercase tracking-[0.18em] text-stone-400">Selected Client Devices</p>
              <p className="mt-2 text-3xl font-semibold">{companyDevices.length}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
              <p className="text-sm font-semibold">Active</p>
              <p className="mt-1 text-2xl font-semibold">{activeDevices.length}</p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
              <p className="text-sm font-semibold">Provisioned</p>
              <p className="mt-1 text-2xl font-semibold">
                {companyDevices.filter((device) => device.status === 'PROVISIONED').length}
              </p>
            </div>
            <div className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
              <p className="text-sm font-semibold">Most Recent Created Device</p>
              <p className="mt-1 text-sm text-[var(--admin-muted)]">
                {companyDevices[0]?.createdAt ?? 'N/A'}
              </p>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default AdminDashboard;
